<script setup>
import { ref, computed, onMounted } from 'vue'
import { planningApi } from '@/apis/planning.api'
import { useWebSiteStore } from '@/stores/web-site.store'

const webSiteStore = useWebSiteStore()
const data = ref(null)

onMounted(async () => {
  const res = await planningApi.getQuarterGlanceAsync()
  data.value = res.data
})

const currencySymbol = computed(() => webSiteStore.getDefaultCurrency()?.symbol ?? '')

const fmt = (val) => {
  if (val == null) return '—'
  return `${currencySymbol.value}${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(val)}`
}

const qoqColor = computed(() => {
  const v = data.value?.qoq_pct
  if (v == null) return 'grey-5'
  if (v > 0) return 'positive'
  if (v < 0) return 'negative'
  return 'warning'
})

const qoqLabel = computed(() => {
  const v = data.value?.qoq_pct
  if (v == null) return 'No prev. data'
  const sign = v > 0 ? '+' : ''
  const arrow = v > 0 ? '▲' : v < 0 ? '▼' : '—'
  return `${arrow} ${sign}${v}% QoQ`
})
</script>

<template>
  <div style="padding: 16px;">
    <div v-if="data === null" style="display:flex; justify-content:center; align-items:center; height:100px;">
      <q-spinner size="md" color="primary" />
    </div>
    <template v-else>
      <div class="text-caption text-grey-6 text-uppercase q-mb-xs" style="letter-spacing:0.08em">
        {{ data.label }}
      </div>

      <div class="qg-revenue text-weight-bold text-primary">{{ fmt(data.revenue) }}</div>
      <div class="text-caption text-grey-6 q-mb-xs">Revenue from completed projects</div>

      <div class="q-mb-xs">
        <q-chip
          :color="qoqColor"
          text-color="white"
          dense
          class="text-weight-bold"
          style="font-size:0.75rem"
        >
          {{ qoqLabel }}
        </q-chip>
      </div>
      <div class="text-caption text-grey-5 q-mb-sm" style="font-style:italic">
        vs {{ fmt(data.prev_revenue) }} in {{ data.prev_label }}
      </div>

      <q-separator class="q-mb-sm" />

      <div style="display:flex; align-items:center; margin-bottom:8px;">
        <span class="qg-stat text-weight-bold" style="margin-right:8px;">{{ data.completed_projects }}</span>
        <q-icon
          name="mdi-check-circle-outline"
          color="positive"
          size="1rem"
          style="margin-right:4px;"
        />
        <span class="text-caption text-grey-6">Completed projects this quarter</span>
      </div>

      <div style="display:flex; align-items:center;">
        <span class="qg-stat text-weight-bold" style="margin-right:8px;">{{ data.quotes_created }}</span>
        <q-icon
          name="mdi-file-document-outline"
          color="primary"
          size="1rem"
          style="margin-right:4px;"
        />
        <span class="text-caption text-grey-6">New quotes this quarter</span>
      </div>
    </template>
  </div>
</template>

<style scoped>
.qg-revenue {
  font-size: clamp(1rem, 10cqw, 2.125rem);
  line-height: 1.15;
  margin-bottom: 4px;
}
.qg-stat {
  font-size: clamp(0.9rem, 7cqw, 1.5rem);
  line-height: 1.2;
}
</style>
